import { ChevronDown, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const languages = [
  { code: "auto", name: "Detect Language" },
  { code: "en", name: "English" },
  { code: "pt", name: "Portuguese (Brazil)" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "German", name: "German" },
  { code: "it", name: "Italian" },
  { code: "Japanese", name: "Japanese" },
  { code: "ko", name: "Korean" },
  { code: "zh", name: "Chinese" },
];

interface LanguageSelectorProps {
  sourceLanguage: string;
  targetLanguage: string;
  onSourceLanguageChange: (language: string) => void;
  onTargetLanguageChange: (language: string) => void;
  onSwapLanguages: () => void;
}

export function LanguageSelector({
  sourceLanguage,
  targetLanguage,
  onSourceLanguageChange,
  onTargetLanguageChange,
  onSwapLanguages,
}: LanguageSelectorProps) {
  const sourceLanguageName = languages.find(lang => lang.code === sourceLanguage)?.name || "Select Language";
  const targetLanguageName = languages.find(lang => lang.code === targetLanguage)?.name || "Select Language";

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 p-3 sm:p-4 bg-translator-panel rounded-lg shadow-soft border border-translator-border">
      <div className="flex-1">
        <Select value={sourceLanguage} onValueChange={onSourceLanguageChange}>
          <SelectTrigger className="w-full bg-secondary border-translator-border hover:bg-muted transition-smooth touch-target">
            <SelectValue placeholder="Source Language">
              {sourceLanguageName}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-translator-panel border-translator-border shadow-medium">
            {languages.map((language) => (
              <SelectItem key={language.code} value={language.code} className="hover:bg-muted">
                {language.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        variant="outline"
        size="icon"
        onClick={onSwapLanguages}
        disabled={sourceLanguage === "auto"}
        className="shrink-0 self-center w-full sm:w-auto h-10 sm:h-10 hover:bg-gradient-primary hover:text-primary-foreground transition-bounce disabled:opacity-50 touch-target"
        title="Swap languages"
        aria-label="Swap languages"
      >
        <ArrowLeftRight className="h-4 w-4 rotate-90 sm:rotate-0" />
      </Button>

      <div className="flex-1">
        <Select value={targetLanguage} onValueChange={onTargetLanguageChange}>
          <SelectTrigger className="w-full bg-secondary border-translator-border hover:bg-muted transition-smooth touch-target">
            <SelectValue placeholder="Target Language">
              {targetLanguageName}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-translator-panel border-translator-border shadow-medium">
            {languages.filter(lang => lang.code !== "auto").map((language) => (
              <SelectItem key={language.code} value={language.code} className="hover:bg-muted">
                {language.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}